import { Avatar, Button, Center, Divider, Flex, Stack, Text } from "@mantine/core";
import { IconBrandWhatsapp, IconPhone } from "@tabler/icons-react";
import moment from "moment";
import "moment/locale/hu";
import "moment/locale/pl";
import "moment/locale/ro";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EOrderStatus, IOrder } from "../interfaces/IOrder";
import classes from "../modules/orderPage.module.css";
import { countryPrefix } from "../utils/dataArrays/countryData";
import PlaceNewOrderModal from "./PlaceNewOrderModal";
import RequestDateChangeModal from "./RequestDateChangeModal";
import { formatDateTime } from "./TimelineCard";
import CardWrapper from "./general/CardWrapper";

interface IInfoCardProps {
  order: IOrder;
  refetch: () => void;
}

export default function InfoCard({ order, refetch }: IInfoCardProps) {
  const { i18n, t } = useTranslation();
  const [isImageValid, setIsImageValid] = useState(true);

  moment.locale(i18n.language);

  const prefix = useMemo(
    () =>
      countryPrefix.find(
        (item) => item.language === order.organizationLanguage?.toUpperCase()
      ),
    [order.organizationLanguage]
  );

  const phone = (order.organizationPhone ?? "").trim();
  const waPhone = `${prefix?.prefix ?? ""}${phone}`.replace(/\s+/g, "");
  const waText = encodeURIComponent(
    t("v2.OrderPage.orderDetails.infoCard.whatsappMessage", {
      code: order.generatedTicketId,
    })
  );
  const isOrderReady =
    order.status === EOrderStatus.FOR_DELIVERY ||
    order.status === EOrderStatus.FOR_DELIVERY_IN_HOUSE;

  return (
    <CardWrapper style={{ paddingInline: 0, height: "100%" }}>
      <Stack w="100%" spacing="var(--sm)">
        <div className={classes.infoCardGrid}>
          {/* CLIENT */}
          <div className={classes.infoBlock}>
            <Text size="sm" fw={700} className={classes.infoLabel}>
              {t("v2.OrderPage.orderDetails.infoCard.client")}
            </Text>

            <div className={classes.infoContent}>
              <Text className={classes.primaryText}>
                {(order.name ?? "-").toUpperCase()}
              </Text>
              <Text className={classes.secondaryText}>
                {order.phone ?? "-"}
              </Text>
            </div>
          </div>

          {/* COMPANY */}
          <div className={classes.infoBlock}>
            <Text size="sm" fw={700} className={classes.infoLabel}>
              {t("v2.OrderPage.orderDetails.infoCard.laundry")}
            </Text>

            {/* Company identity */}
            <Flex align="center" gap="var(--sm)">
              <Center
                className={classes.companyLogoBox}
                style={{ background: "var(--divider)" }}
              >
                {order.organizationLogo && isImageValid ? (
                  <img
                    onError={() => setIsImageValid(false)}
                    src={order.organizationLogo}
                    className={classes.companyLogoImg}
                    alt={t("ImageAlts.image")}
                  />
                ) : (
                  <Avatar
                    styles={{
                      placeholder: {
                        color: "var(--bodyLight)",
                        background: "none",
                        textTransform: "uppercase",
                        fontSize: 20,
                      },
                    }}
                    w="100%"
                    h="100%"
                    radius={10}
                  >
                    {order.organizationName?.slice(0, 2)}
                  </Avatar>
                )}
              </Center>

              <div className={classes.companyText}>
                <Text
                  className={classes.primaryText}
                  title={order.organizationName ?? ""}
                >
                  {order.organizationName ?? "-"}
                </Text>
                <Text className={classes.secondaryText}>
                  {order.organizationPhone ?? "-"}
                </Text>
              </div>
            </Flex>

            {/* SECONDARY ACTIONS */}
            <Stack spacing="xs" className={classes.companyActionsStack}>
              <Button
                component="a"
                href={`tel:${phone}`}
                leftIcon={<IconPhone size={16} />}
                variant="outline"
                radius="md"
                size="sm"
                fullWidth
                style={{
                  borderColor: "var(--accent)",
                  //text same color as accent
                  color: "var(--accent)",
                  borderWidth: 2,
                }}
              >
                {t("callOrganization")}
              </Button>

              <Button
                component="a"
                href={`https://wa.me/${waPhone}?text=${waText}`}
                leftIcon={<IconBrandWhatsapp size={16} />}
                variant="outline"
                radius="md"
                size="sm"
                fullWidth
                className={classes.whatsAppOutline}
              >
                {t("openWhatsApp")}
              </Button>

              {isOrderReady && order.abilityToAddPreferredDeliveryDate && (
                <>
                  <Divider />
                  <RequestDateChangeModal
                    isRouteDelivery={Boolean(order.aproximateHourDelivery)}
                    refetch={refetch}
                    id={order.generatedTicketId}
                    details={order?.deliveryDateChanged}
                    disabled={Boolean(order.deliveryDateChanged)}
                  />
                </>

              )}

              {/* DELIVERY DATE CHANGE INFO */}
              {order.deliveryDateChanged && order.abilityToAddPreferredDeliveryDate && (
                <div className={classes.deliveryChangeInfo}>
                  <Text size="sm" fw={600} color="var(--green)">
                    {t("v2.OrderPage.orderDetails.infoCard.requestSent")}
                  </Text>

                  <Text size="sm" className={classes.deliveryChangeMeta}>
                    {formatDateTime(order.deliveryDateChanged.date)}
                    {order.deliveryDateChanged.time
                      ? ` · ${order.deliveryDateChanged.time}`
                      : ""}
                  </Text>

                  {order.deliveryDateChanged.reason && (
                    <Text size="sm" className={classes.deliveryChangeReason}>
                      <strong>{t(
                        "v2.OrderPage.orderDetails.infoCard.requestModal.inputs.reason",
                      )}:</strong>{" "}
                      {order.deliveryDateChanged.reason}
                    </Text>
                  )}
                </div>
              )}
            </Stack>
            <Divider m={10} />
            {/* MAIN CTA */}
            {order?.canPlaceOrderFromDigitalTicket && (
              <div className={classes.placeOrderRow}>
                <PlaceNewOrderModal
                  organizationShortId={order.organizationShortId!}
                  orderId={order.generatedTicketId}
                />
              </div>
            )}
          </div>
        </div>
      </Stack>
    </CardWrapper>
  );
}
